import React, { useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDebounce } from 'use-debounce';

import BlockSection from '~/components/wrappers/BlockSection';

import AnythingCard from '~/components/cards/AnythingCard';

import useWindowScrollPosition from '~/hooks/useWindowScrollPosition';
import useDragScroll from '~/hooks/useDragScroll';
import useWindowSize from '~/hooks/useWindowSize';

import { calcVw } from '~/styles/helpers';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes
} from '~/utilities/propTypes';

const OuterContainer = styled.div`
    position: relative;
    width: ${calcVw(100)};
    left: 50%;
    right: 50%;
    margin: 0 ${calcVw(-50)};
    overflow: hidden;
`;

const ScrollableContainer = styled.div`
    transform: translate3d(0, 0, 0);
    width: ${calcVw(100)};
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        position: relative;
        z-index: 2;
        width: auto;
        display: inline-flex;
        overflow-x: visible;
        transform: translate3d(
            -${({ scrollPercent }) => scrollPercent * 100}%,
            0,
            0
        );
    }
`;
const InnerContainer = styled.div`
    display: flex;
    width: auto;
    padding: 0 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding: 0 0 0 70vw;
    }
`;

const CardsWrapper = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 15px;
    padding: 10px 0;
`;

const AnythingCards = ({
    attributes = DefaultAcfAttributes,
    globalBlockOptions = DefaultGlobalBlockOptions,
    cards
}) => {
    const { y: scrollY } = useWindowScrollPosition();
    const [intersectionRatio, setIntersectionRatio] = useState(0);
    const [scrollPercent] = useDebounce(intersectionRatio, 1);
    const windowSizes = useWindowSize();
    const [ref, node] = useDragScroll(!windowSizes.includes('desktop'));

    useLayoutEffect(() => {
        if (node) {
            const { top, height } = node.getBoundingClientRect();

            if (Math.abs(top) <= window.innerHeight) {
                const scrollDistance = Math.abs(
                    1 - (top + height / 2) / (height * cards.length)
                );
                setIntersectionRatio(
                    scrollDistance > 0 && scrollDistance < 1
                        ? scrollDistance
                        : 1
                );
            } else {
                setIntersectionRatio(0);
            }
        }
    }, [node, scrollY, cards.length]);

    return (
        <BlockSection {...{ globalBlockOptions, attributes }}>
            <OuterContainer>
                <ScrollableContainer {...{ ref, scrollPercent }}>
                    <InnerContainer>
                        <CardsWrapper>
                            {cards.map((card) => (
                                <AnythingCard {...card} key={card.title} />
                            ))}
                        </CardsWrapper>
                    </InnerContainer>
                </ScrollableContainer>
            </OuterContainer>
        </BlockSection>
    );
};

AnythingCards.propTypes = {
    attributes: AcfAttributes,
    globalBlockOptions: GlobalBlockOptions,
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            badge: PropTypes.string,
            description: PropTypes.string.isRequired,
            image: PropTypes.shape({
                sourceUrl: PropTypes.string.isRequired,
                altText: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.shape({
                        gatsbyImageData: PropTypes.object.isRequired
                    }).isRequired
                })
            }).isRequired,
            link: PropTypes.shape({
                url: PropTypes.string.isRequired,
                target: PropTypes.string
            }).isRequired
        })
    ).isRequired
};

export default AnythingCards;
